import i18n from "@/plugins/i18n";
import ApiService from "@/api";
import moment from "moment";
import queryString from "query-string";
import { map, replace, uniqBy } from "lodash";
import arrayToTree from "array-to-tree";

// initial state
const state = () => ({
    lang: "fr",
    loading: false,
    general: {},
    landingPage: {},
    offers: [],
    faq: [],
    weTalkAboutUs: [],
    aboutUs: {},
    page: {},
    newsHome: [],
    currency: {
        code: "EUR",
        symbol: "€"
    },
    location: {
        lat: null,
        lng: null
    },
    areInCampings: [],
    menu: {
        menuLeft: [],
        menuRight: [],
        menuFooter: [],
        menuPages: [],
        menus: []
    }
});

// getters
const getters = {};

// actions
const actions = {
    getCurrentPosition({ commit }) {
        navigator.geolocation.getCurrentPosition(
            position => {
                commit("setLocation", {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                });
            },
            error => {
                console.log(error.message);
            }
        );
    },
    watchPosition({ commit }) {
        let options;
        function success(pos) {
            commit("setLocation", {
                lat: pos.coords.latitude,
                lng: pos.coords.longitude
            });
        }

        function error(err) {
            console.warn("ERROR(" + err.code + "): " + err.message);
        }
        options = {
            enableHighAccuracy: false,
            timeout: 5000,
            maximumAge: 0
        };
        navigator.geolocation.watchPosition(success, error, options);
    },
    getPageTitle({ commit, state }) {
        return new Promise(resolve => {
            let url = `/wp-json/wp/v2/site-title`;
            if (state.lang === "en") {
                url = `/en/wp-json/wp/v2/site-title`;
            }
            ApiService.get(url)
                .then(({ data }) => {
                    commit("setPageTitle", data);
                    resolve(data);
                })
                .catch(() => {});
        });
    },
    getNewsHome({ commit, state }) {
        commit("setLoading", true);
        let url = `/get-news-home`;
        if (state.lang === "en") {
            url = `/en/get-news-home`;
        }

        return new Promise(resolve => {
            ApiService.get(url)
                .then(({ data }) => {
                    commit("setNewsHome", data);
                    commit("setLoading", false);
                    resolve(data);
                })
                .catch(() => {
                    commit("setLoading", false);
                });
        });
    },
    getPage({ commit, state }, pageId) {
        commit("setLoading", true);
        commit("setPage", {});
        // The API serves the translation of this page in the requested language.
        let url = `/wp-json/wp/v2/pages/${pageId}`;
        if (state.lang === "en") {
            url = `/en${url}`;
        }
        return new Promise(resolve => {
            ApiService.get(url)
                .then(({ data }) => {
                    commit("setLoading", false);
                    commit("setPage", data);
                    resolve(data);
                })
                .catch(() => {
                    commit("setLoading", false);
                });
        });
    },
    getMenuLeft({ commit, state }) {
        return new Promise(resolve => {
            let url = `/wp-json/wp/v2/menu-left`;
            if (state.lang === "en") {
                url = `en/wp-json/wp/v2/menu-left`;
            }
            ApiService.get(url)
                .then(({ data }) => {
                    commit("setMenuLeft", data);
                    resolve(data);
                })
                .catch(() => {});
        });
    },
    getMenuRight({ commit, state }) {
        return new Promise(resolve => {
            let url = `/wp-json/wp/v2/menu-right`;
            if (state.lang === "en") {
                url = `en/wp-json/wp/v2/menu-right`;
            }
            ApiService.get(url)
                .then(({ data }) => {
                    commit("setMenuRight", data);
                    resolve(data);
                })
                .catch(() => {});
        });
    },
    getMenuFooter({ commit, state }) {
        return new Promise(resolve => {
            let url = `/wp-json/wp/v2/menu-footer`;
            if (state.lang === "en") {
                url = `en/wp-json/wp/v2/menu-footer`;
            }
            ApiService.get(url)
                .then(({ data }) => {
                    commit("setMenuFooter", data);
                    resolve(data);
                })
                .catch(() => {});
        });
    },
    getMenuPages({ commit, state }) {
        return new Promise(resolve => {
            let url = `/wp-json/wp/v2/menu-pages`;
            if (state.lang === "en") {
                url = `en/wp-json/wp/v2/menu-pages`;
            }
            ApiService.get(url)
                .then(({ data }) => {
                    commit("setMenuPages", data);
                    resolve(data);
                })
                .catch(() => {});
        });
    },
    getAboutUs({ commit, state }) {
        commit("setLoading", true);
        let url = `/wp-json/wp/v2/pages/243`;
        if (state.lang === "en") {
            url = `/en${url}`;
        }
        return new Promise(resolve => {
            ApiService.get(url)
                .then(({ data }) => {
                    commit("setLoading", false);
                    commit("setAboutUs", data);
                    resolve(data);
                })
                .catch(() => {
                    commit("setLoading", false);
                });
        });
    },
    getWeTalkAboutUs({ commit, state }) {
        commit("setLoading", true);
        let url = `on-parle-de-nous`;
        if (state.lang === "en") {
            url = `en/on-parle-de-nous`;
        }

        return new Promise(resolve => {
            ApiService.get(url)
                .then(({ data }) => {
                    commit("setLoading", false);
                    commit("setWeTalkAboutUs", data);
                    resolve(data);
                })
                .catch(() => {
                    commit("setLoading", false);
                });
        });
    },
    getFAQ({ commit, state }) {
        commit("setLoading", true);
        let url = `get-faqs`;
        if (state.lang === "en") {
            url = `en/get-faqs`;
        }

        return new Promise(resolve => {
            ApiService.get(url)
                .then(({ data }) => {
                    commit("setLoading", false);
                    commit("setFAQ", data);
                    resolve(data);
                })
                .catch(() => {
                    commit("setLoading", false);
                });
        });
    },
    getOffers({ commit, state }) {
        commit("setLoading", true);
        let url = `wp-json/wp/v2/offres`;
        if (state.lang === "en") {
            url = `en/wp-json/wp/v2/offres`;
        }

        return new Promise(resolve => {
            ApiService.get(url)
                .then(({ data }) => {
                    commit("setLoading", false);
                    commit("setOffers", data);
                    resolve(data);
                })
                .catch(() => {
                    commit("setLoading", false);
                });
        });
    },
    sendFeedback({ commit }, params) {
        commit("setLoading", true);
        const stringified = queryString.stringify(params);
        return new Promise(resolve => {
            ApiService.get(`/feedback/?${stringified}`)
                .then(({ data }) => {
                    commit("setLoading", false);
                    resolve(data);
                })
                .catch(() => {
                    commit("setLoading", false);
                });
        });
    },

    checkFeedback({ commit }, params) {
        commit("setLoading", true);
        const stringified = queryString.stringify(params);
        return new Promise(resolve => {
            ApiService.get(`/feedback/?${stringified}`)
                .then(({ data }) => {
                    commit("setLoading", false);
                    resolve(data);
                })
                .catch(() => {
                    commit("setLoading", false);
                });
        });
    },
    getGeneral({ commit, state }) {
        commit("setLoading", true);
        let url = `get-options`;
        if (state.lang === "en") {
            url = `en/get-options`;
        }
        return new Promise(resolve => {
            ApiService.get(url)
                .then(({ data }) => {
                    commit("setGeneral", data);
                    commit("setLoading", false);
                    resolve(data);
                })
                .catch(() => {
                    commit("setLoading", true);
                });
        });
    },
    getLandingPage({ commit, state }) {
        commit("setLoading", true);
        let url = `wp-json/acf/v3/pages/33`;
        if (state.lang === "en") {
            url = `en/wp-json/acf/v3/pages/188`;
        }
        return new Promise(resolve => {
            ApiService.get(url)
                .then(({ data: { acf } }) => {
                    commit("setLandingPage", acf);
                    commit("setLoading", false);
                    resolve(acf);
                })
                .catch(() => {
                    commit("setLoading", true);
                });
        });
    },
    sendContact({ commit }, params) {
        commit("setLoading", true);
        return new Promise(resolve => {
            const stringified = queryString.stringify(params);
            ApiService.get(`contact-us/?${stringified}`)
                .then(({ data }) => {
                    commit("setLoading", false);
                    resolve(data);
                })
                .catch(() => {
                    commit("setLoading", false);
                });
        });
    },
    setCurrency({ commit }, data) {
        commit("setCurrency", data);
    }
};

// mutations
const mutations = {
    setAreInCampings(state, data) {
        state.areInCampings = data;
    },
    setLocation(state, location) {
        state.location = location;
    },
    setLang(state, lang) {
        state.lang = lang;
        i18n.locale = lang;
        moment.locale(lang);
    },
    setGeneral(state, data) {
        state.general = {
            ...state.general,
            ...data
        };
    },
    setLoading(state, loading) {
        state.loading = loading;
    },
    setLandingPage(state, data) {
        state.landingPage = data;
    },
    setOffers(state, data) {
        state.offers = data;
    },
    setFAQ(state, data) {
        state.faq = data;
    },
    setWeTalkAboutUs(state, data) {
        state.weTalkAboutUs = data;
    },
    setAboutUs(state, data) {
        state.aboutUs = data;
    },
    setPage(state, data) {
        state.page = data;
    },
    setNewsHome(state, data) {
        state.newsHome = data;
    },
    setCurrency(state, data) {
        state.currency = data;
    },
    setMenuLeft(state, data) {
        let newData = map(data, item => {
            const pathObject = item.url ? new URL(item.url) : {};
            let path = pathObject.pathname || "";
            if (path.split("/").length > 2) {
                path = path.split("/");
                path.pop();
                path = path.join("/");
            }
            return {
                ...item,
                path: replace(path, "/en", "")
            };
        });
        const menus = newData;
        newData = arrayToTree(newData, {
            parentProperty: "menu_item_parent",
            customID: "ID"
        });
        state.menu = {
            ...state.menu,
            menuLeft: newData,
            menus: uniqBy([...state.menu.menus, ...menus], "ID")
        };
    },
    setMenuRight(state, data) {
        let newData = map(data, item => {
            const pathObject = item.url ? new URL(item.url) : {};
            let path = pathObject.pathname || "";
            if (path.split("/").length > 2) {
                path = path.split("/");
                path.pop();
                path = path.join("/");
            }
            return {
                ...item,
                path: replace(path, "/en", "")
            };
        });
        const menus = newData;
        newData = arrayToTree(newData, {
            parentProperty: "menu_item_parent",
            customID: "ID"
        });
        state.menu = {
            ...state.menu,
            menuRight: newData,
            menus: uniqBy([...state.menu.menus, ...menus], "ID")
        };
    },
    setMenuFooter(state, data) {
        let newData = map(data, item => {
            const pathObject = item.url ? new URL(item.url) : {};
            let path = pathObject.pathname || "";
            if (path.split("/").length > 2) {
                path = path.split("/");
                path.pop();
                path = path.join("/");
            }
            return {
                ...item,
                path: replace(path, "/en", "")
            };
        });
        const menus = newData;
        state.menu = {
            ...state.menu,
            menuFooter: newData,
            menus: uniqBy([...state.menu.menus, ...menus], "ID")
        };
    },
    setMenuPages(state, data) {
        let newData = map(data, item => {
            const pathObject = item.url ? new URL(item.url) : {};
            let path = pathObject.pathname || "";
            if (path.split("/").length > 2) {
                path = path.split("/");
                path.pop();
                path = path.join("/");
            }
            return {
                ...item,
                path: replace(path, "/en", "")
            };
        });
        const menus = newData;
        state.menu = {
            ...state.menu,
            menuPages: newData,
            menus: uniqBy([...state.menu.menus, ...menus], "ID")
        };
    },
    setPageTitle(state, title) {
        state.general = {
            ...state.general,
            title
        };
    },
    setDeviceToken(state, token) {
        state.token = token;
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};
